import { useRef, useState } from "react";
import texasSvgUrl from "../../assets/texas.svg?url";
import { api } from "../../state/api";
import {
  type ClusteredLocation,
  clusterLocations,
  transformLocationData,
} from "../../utilities/analyticsHelpers";
import { ChartCard } from "./ChartCard";

type LocationsCardProps = {
  appUUID: string;
  className?: string;
};

// From the attached `texas.svg`
const TEXAS_VIEWBOX_WIDTH = 334;
const TEXAS_VIEWBOX_HEIGHT = 304;

const TEXAS_BOUNDS = {
  minLat: 25.8,
  maxLat: 36.6,
  minLng: -106.7,
  maxLng: -93.3,
} as const;

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function projectTexasLngLatToSvg(
  lng: number,
  lat: number,
  width: number,
  height: number,
  padding: number,
) {
  const x01 =
    (lng - TEXAS_BOUNDS.minLng) / (TEXAS_BOUNDS.maxLng - TEXAS_BOUNDS.minLng);
  const y01 =
    1 -
    (lat - TEXAS_BOUNDS.minLat) / (TEXAS_BOUNDS.maxLat - TEXAS_BOUNDS.minLat);

  const x = padding + clamp(x01, 0, 1) * (width - padding * 2);
  const y = padding + clamp(y01, 0, 1) * (height - padding * 2);

  return { x, y };
}

function TexasLocationsMap({ locations }: { locations: ClusteredLocation[] }) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [tooltip, setTooltip] = useState<{
    loc: ClusteredLocation;
    x: number;
    y: number;
  } | null>(null);

  const width = TEXAS_VIEWBOX_WIDTH;
  const height = TEXAS_VIEWBOX_HEIGHT;
  const padding = 10;

  const texasOnly = locations.filter(
    (l) =>
      l.lat >= TEXAS_BOUNDS.minLat &&
      l.lat <= TEXAS_BOUNDS.maxLat &&
      l.lng >= TEXAS_BOUNDS.minLng &&
      l.lng <= TEXAS_BOUNDS.maxLng,
  );

  const maxCount = texasOnly.reduce((max, l) => Math.max(max, l.count), 0);
  const minR = 5;
  const maxR = 18;

  function updateTooltipPosition(e: React.MouseEvent) {
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setTooltip((prev) => {
      if (!prev) return prev;
      return { ...prev, x, y };
    });
  }

  return (
    <div ref={wrapperRef} className="relative h-full min-h-0 w-full">
      <svg
        className="inset-0 block h-full w-full lg:absolute"
        viewBox={`0 0 ${String(width)} ${String(height)}`}
        role="img"
        aria-label="Texas map with location markers"
        preserveAspectRatio="xMidYMid meet"
        onMouseLeave={() => {
          setTooltip(null);
        }}
        onMouseMove={updateTooltipPosition}
      >
        <image
          href={texasSvgUrl}
          x={0}
          y={0}
          width={width}
          height={height}
          preserveAspectRatio="xMidYMid meet"
        />

        <g>
          {texasOnly.map((loc) => {
            const { x, y } = projectTexasLngLatToSvg(
              loc.lng,
              loc.lat,
              width,
              height,
              padding,
            );

            const normalized =
              maxCount > 0 ? Math.sqrt(loc.count) / Math.sqrt(maxCount) : 0;
            const r = minR + normalized * (maxR - minR);

            return (
              <circle
                key={loc.id}
                cx={x}
                cy={y}
                r={r}
                fill="#4DE7FF"
                className="mix-blend-multiply"
                stroke="#0090A8"
                strokeWidth={2}
                onMouseEnter={(e) => {
                  const rect = wrapperRef.current?.getBoundingClientRect();
                  if (!rect) return;

                  setTooltip({
                    loc,
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top,
                  });
                }}
              />
            );
          })}
        </g>
      </svg>

      {tooltip ? (
        <div
          className="pointer-events-none absolute z-10"
          style={{
            left: tooltip.x + 12,
            top: tooltip.y - 12,
            transform: "translateY(-100%)",
          }}
        >
          <div className="rounded-lg border border-secondary bg-primary p-lg shadow-lg">
            <p className="font-mono text-[10px] leading-[12px] font-semibold text-secondary">
              {tooltip.loc.name}: {tooltip.loc.count}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function LocationsCard({ appUUID, className }: LocationsCardProps) {
  const {
    data: locationsResponse,
    isLoading,
    error,
  } = api.useGetAppLocationsQuery({
    appUUID,
  });

  const hasError = !!error;
  const data = locationsResponse?.data ?? [];
  const locations = transformLocationData(data);
  const clusteredLocations = clusterLocations(locations, 5);

  return (
    <ChartCard title="Locations" hasError={hasError} className={className}>
      {isLoading ? (
        <div className="flex h-43 min-h-0 flex-1 items-center justify-center lg:h-auto">
          <div className="h-full w-full animate-pulse rounded bg-secondary" />
        </div>
      ) : (
        <div className="flex h-43 min-h-0 min-h-[8.125rem] w-full flex-1 rounded-b bg-primary px-3xl lg:h-auto">
          <TexasLocationsMap locations={clusteredLocations} />
        </div>
      )}
    </ChartCard>
  );
}
